import { useState } from "react";

import { DATE_FORMAT } from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { queryClient } from "@impero-com/common/utils/query_client";
import { emitNotification, TextToolState } from "@impero-com/ui";
import { Row } from "@tanstack/react-table";
import moment from "moment-timezone";

import {
    postControlResultsReopen,
    getGetControlResultsInfiniteQueryKey,
} from "api/generated/control-results/control-results";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import { ControlActivityTableRow } from "./types";
import {
    CALENDAR_DAY_FORMAT,
    CalendarDay,
    computeSelectionDueDates,
    newDueDateInstant,
} from "./utils/extendDueDate";

function isReopenable(row: ControlActivityTableRow): boolean {
    return (
        !row.archived &&
        row.canEditProgram &&
        (row.workflowStatus === "Completed" ||
            row.workflowStatus === "CompletedAfterDueDate")
    );
}

function computeReopenToolState(
    t: TFunction,
    hasSelection: boolean,
    hasArchivedSelection: boolean,
    isCompleted: boolean,
    hasPermission: boolean
): TextToolState {
    if (!hasSelection) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_NoSelection"),
        };
    }
    if (hasArchivedSelection) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Reopen_Tooltip_Archived"),
        };
    }
    if (!isCompleted) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Reopen_Tooltip_NotCompleted"),
        };
    }
    if (!hasPermission) {
        return {
            type: "DISABLED",
            reason: t("ControlActivitiesList_Tooltip_Reopen_NoPermission"),
        };
    }
    return { type: "ENABLED" };
}

interface UseControlActivityReopenUtilsProps {
    selectedRows: Row<ControlActivityTableRow>[];
    deselectAll: () => void;
}

export function useControlActivityReopenUtils({
    selectedRows,
    deselectAll,
}: UseControlActivityReopenUtilsProps) {
    const { t } = useTranslation();
    const timezoneName = useCurrentUserTimezone();
    const [reopenModalOpen, setReopenModalOpen] = useState(false);
    const [isReopening, setIsReopening] = useState(false);

    const rows = selectedRows.map((row) => row.original);
    const reopenableIds = rows
        .filter(isReopenable)
        .map((row) => row.activityId);
    const selectionDueDates = computeSelectionDueDates(rows);
    const singleActivityTitle = rows.length === 1 ? rows[0].title : null;
    const isCompleted = selectedRows.every(
        ({ original }) =>
            original.workflowStatus === "Completed" ||
            original.workflowStatus === "CompletedAfterDueDate"
    );
    const hasArchivedSelection = selectedRows.some(
        (row) => row.original.archived
    );
    const hasPermission = selectedRows.every(
        (row) => row.original.canEditProgram
    );

    const reopenToolState = computeReopenToolState(
        t,
        selectedRows.length > 0,
        hasArchivedSelection,
        isCompleted,
        hasPermission
    );

    const openReopenModal = () => setReopenModalOpen(true);
    const closeReopenModal = () => setReopenModalOpen(false);

    const handleReopen = async (
        comment: string,
        newDueDay: CalendarDay | null
    ) => {
        setIsReopening(true);
        const trimmedComment = comment.trim();
        try {
            await postControlResultsReopen({
                controlResultIds: reopenableIds,
                ...(trimmedComment !== "" && { comment: trimmedComment }),
                ...(newDueDay !== null && {
                    newDueDate: newDueDateInstant(newDueDay, timezoneName),
                }),
            });
            await queryClient.invalidateQueries({
                queryKey: getGetControlResultsInfiniteQueryKey(),
            });
            const count = reopenableIds.length;
            emitNotification(
                "success",
                newDueDay !== null
                    ? t("ControlActivitiesList_ReopenAndExtend_Success", {
                          count,
                          date: moment(newDueDay, CALENDAR_DAY_FORMAT).format(
                              DATE_FORMAT
                          ),
                      })
                    : t("ControlActivitiesList_Reopen_Success", { count })
            );
            deselectAll();
            closeReopenModal();
        } catch {
            // Kept open so the user can retry.
            emitNotification("error", t("ControlActivitiesList_Reopen_Failed"));
        } finally {
            setIsReopening(false);
        }
    };

    return {
        reopenToolState,
        selectionDueDates,
        singleActivityTitle,
        reopenModalOpen,
        openReopenModal,
        closeReopenModal,
        handleReopen,
        isReopening,
    };
}
